<template>
  <div>
    <!-- Valores do Pedido -->
    <CCard class="mb-3">
      <CCardHeader class="section-header">
        <fa-icon icon="dollar-sign" class="mr-2" />
        <strong>Valores do Pedido</strong>
      </CCardHeader>
      <CCardBody>
        <div class="row">
          <div class="col-md-4">
            <div class="info-item">
              <label>
                <fa-icon icon="calculator" class="mr-1 text-muted" />
                Subtotal
              </label>
              <div class="info-value">{{ formatMoney(orcamento.subtotal) }}</div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="info-item">
              <label>
                <fa-icon icon="percentage" class="mr-1 text-muted" />
                Descontos
              </label>
              <div class="info-value text-danger">
                {{ formatMoney(orcamento.valortotalsemdesconto ? orcamento.valortotalsemdesconto - orcamento.valortotal : 0) }}
                <span v-if="orcamento.percentualdesconto" class="text-muted ml-1">
                  ({{ orcamento.percentualdesconto }}%)
                </span>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="info-item">
              <label>
                <fa-icon icon="gift" class="mr-1 text-muted" />
                Bonificação
              </label>
              <div class="info-value text-warning">{{ formatMoney(orcamento.valorbonificado) }}</div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="info-item">
              <label>
                <fa-icon icon="file-invoice-dollar" class="mr-1 text-muted" />
                Impostos
              </label>
              <div class="info-value">{{ formatMoney(orcamento.valorimpostos) }}</div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="info-item">
              <label>
                <fa-icon icon="truck" class="mr-1 text-muted" />
                Frete
              </label>
              <div class="info-value">{{ formatMoney(orcamento.valorfrete) }}</div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="info-item">
              <label>
                <fa-icon icon="hand-holding-usd" class="mr-1 text-muted" />
                Taxa Fixa
              </label>
              <div class="info-value">{{ formatMoney(orcamento.valortaxafixa) }}</div>
            </div>
          </div>
          <div class="col-md-4" v-if="orcamento.valorcredito">
            <div class="info-item">
              <label>
                <fa-icon icon="coins" class="mr-1 text-muted" />
                Crédito Aplicado
              </label>
              <div class="info-value text-success">{{ formatMoney(orcamento.valorcredito) }}</div>
            </div>
          </div>
          <div class="col-md-4" v-if="orcamento.valor_entrada">
            <div class="info-item">
              <label>
                <fa-icon icon="money-bill-wave" class="mr-1 text-muted" />
                Valor de Entrada
              </label>
              <div class="info-value">{{ formatMoney(orcamento.valor_entrada) }}</div>
            </div>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-md-12">
            <div class="total-pedido">
              <div class="total-info">
                <span class="total-label">VALOR TOTAL DO PEDIDO</span>
                <span class="total-subtitle">Valor final com todos os ajustes aplicados</span>
              </div>
              <span class="total-value">{{ formatMoney(orcamento.valortotal) }}</span>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Frete e Entrega -->
    <CCard class="mb-3" v-if="orcamento.transportadora || orcamento.tipoFrete || orcamento.valorfrete_cotacao">
      <CCardHeader class="section-header">
        <fa-icon icon="shipping-fast" class="mr-2" />
        <strong>Frete e Entrega</strong>
      </CCardHeader>
      <CCardBody>
        <div class="row">
          <div class="col-md-6" v-if="orcamento.transportadora">
            <div class="info-item">
              <label>
                <fa-icon icon="truck-loading" class="mr-1 text-muted" />
                Transportadora
              </label>
              <div class="info-value">
                <CBadge color="info">{{ orcamento.transportadora.desc || orcamento.transportadora.id }}</CBadge>
              </div>
            </div>
          </div>
          <div class="col-md-6" v-if="orcamento.tipoFrete">
            <div class="info-item">
              <label>
                <fa-icon icon="dolly" class="mr-1 text-muted" />
                Tipo de Frete
              </label>
              <div class="info-value">{{ orcamento.tipoFrete.desc }}</div>
            </div>
          </div>
          <div class="col-md-6" v-if="orcamento.valorfrete_cotacao">
            <div class="info-item">
              <label>
                <fa-icon icon="calculator" class="mr-1 text-muted" />
                Frete Cotação
              </label>
              <div class="info-value">{{ formatMoney(orcamento.valorfrete_cotacao) }}</div>
            </div>
          </div>
          <div class="col-md-6" v-if="orcamento.retira">
            <div class="info-item">
              <label>
                <fa-icon icon="hand-holding" class="mr-1 text-muted" />
                Retirada
              </label>
              <div class="info-value">
                <CBadge color="warning">Cliente irá retirar</CBadge>
              </div>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Informações Comerciais -->
    <CCard class="mb-3">
      <CCardHeader class="section-header">
        <fa-icon icon="chart-line" class="mr-2" />
        <strong>Informações Comerciais</strong>
      </CCardHeader>
      <CCardBody>
        <div class="row">
          <div class="col-md-4" v-if="orcamento.perc_lucro !== undefined && orcamento.perc_lucro !== null">
            <div class="info-item">
              <label>
                <fa-icon icon="percentage" class="mr-1 text-muted" />
                Margem de Lucro
              </label>
              <div class="info-value">
                <CBadge :color="getLucroColor(orcamento.perc_lucro)">
                  {{ orcamento.perc_lucro }}%
                </CBadge>
              </div>
            </div>
          </div>
          <div class="col-md-4" v-if="orcamento.tabelaPreco">
            <div class="info-item">
              <label>
                <fa-icon icon="list-alt" class="mr-1 text-muted" />
                Tabela de Preço
              </label>
              <div class="info-value">{{ orcamento.tabelaPreco }}</div>
            </div>
          </div>
          <div class="col-md-4" v-if="orcamento.prioridade">
            <div class="info-item">
              <label>
                <fa-icon icon="exclamation-circle" class="mr-1 text-muted" />
                Prioridade
              </label>
              <div class="info-value">
                <CBadge color="danger">{{ orcamento.prioridade }}</CBadge>
              </div>
            </div>
          </div>
          <div class="col-md-4" v-if="orcamento.ordem_compra">
            <div class="info-item">
              <label>
                <fa-icon icon="file-alt" class="mr-1 text-muted" />
                Ordem de Compra
              </label>
              <div class="info-value">{{ orcamento.ordem_compra }}</div>
            </div>
          </div>
          <div class="col-md-4" v-if="orcamento.cupom">
            <div class="info-item">
              <label>
                <fa-icon icon="ticket-alt" class="mr-1 text-muted" />
                Cupom Aplicado
              </label>
              <div class="info-value">
                <CBadge color="success">{{ orcamento.cupom.codigo }}</CBadge>
              </div>
            </div>
          </div>
          <div class="col-md-4" v-if="orcamento.campanhacomercial_id">
            <div class="info-item">
              <label>
                <fa-icon icon="bullhorn" class="mr-1 text-muted" />
                Campanha
              </label>
              <div class="info-value">
                <CBadge color="info">ID: {{ orcamento.campanhacomercial_id }}</CBadge>
              </div>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Observações -->
    <CCard class="mb-3" v-if="orcamento.observacao_pedido || orcamento.observacao_cliente || orcamento.observacao_entrega">
      <CCardHeader class="section-header">
        <fa-icon icon="comment-alt" class="mr-2" />
        <strong>Observações</strong>
      </CCardHeader>
      <CCardBody>
        <div class="row">
          <div class="col-md-12" v-if="orcamento.observacao_pedido">
            <div class="obs-item">
              <label>
                <fa-icon icon="file-alt" class="mr-1" />
                Observação do Pedido
              </label>
              <div class="obs-content">{{ orcamento.observacao_pedido }}</div>
            </div>
          </div>
          <div class="col-md-12" v-if="orcamento.observacao_cliente">
            <div class="obs-item">
              <label>
                <fa-icon icon="user-comment" class="mr-1" />
                Observação do Cliente
              </label>
              <div class="obs-content">{{ orcamento.observacao_cliente }}</div>
            </div>
          </div>
          <div class="col-md-12" v-if="orcamento.observacao_entrega">
            <div class="obs-item">
              <label>
                <fa-icon icon="shipping-fast" class="mr-1" />
                Observação de Entrega
              </label>
              <div class="obs-content">{{ orcamento.observacao_entrega }}</div>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Tracking (UTM) -->
    <CCard class="mb-3" v-if="orcamento.utm_source || orcamento.utm_medium || orcamento.utm_campaign">
      <CCardHeader class="section-header">
        <fa-icon icon="chart-bar" class="mr-2" />
        <strong>Rastreamento (UTM)</strong>
      </CCardHeader>
      <CCardBody>
        <div class="row">
          <div class="col-md-4" v-if="orcamento.utm_source">
            <div class="info-item">
              <label>UTM Source</label>
              <div class="info-value">
                <code>{{ orcamento.utm_source }}</code>
              </div>
            </div>
          </div>
          <div class="col-md-4" v-if="orcamento.utm_medium">
            <div class="info-item">
              <label>UTM Medium</label>
              <div class="info-value">
                <code>{{ orcamento.utm_medium }}</code>
              </div>
            </div>
          </div>
          <div class="col-md-4" v-if="orcamento.utm_campaign">
            <div class="info-item">
              <label>UTM Campaign</label>
              <div class="info-value">
                <code>{{ orcamento.utm_campaign }}</code>
              </div>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
export default {
  name: "financeiro-orcamento",
  props: ["orcamento"],
  methods: {
    formatMoney(value) {
      if (!value && value !== 0) return 'R$ 0,00';
      const num = parseFloat(value);
      return 'R$ ' + num.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    },
    getLucroColor(perc) {
      if (perc < 10) return 'danger';
      if (perc < 20) return 'warning';
      return 'success';
    }
  }
};
</script>

<style scoped>
/* Section Headers */
.section-header {
  background: #f8f9fa;
  color: #2d3748;
  font-weight: 600;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #dee2e6;
}

.section-header .svg-inline--fa {
  color: #6c757d;
  margin-right: 0.5rem;
}

/* Cards */
.card {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
}

/* Info Items */
.info-item {
  margin-bottom: 1.5rem;
}

.info-item label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 0.9375rem;
  color: #212529;
  padding: 0.5rem 0;
  font-weight: 500;
}

/* Total Pedido */
.total-pedido {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 0.25rem;
  border: 2px solid #28a745;
}

.total-info {
  display: flex;
  flex-direction: column;
}

.total-label {
  font-size: 1rem;
  font-weight: 700;
  color: #212529;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.total-subtitle {
  font-size: 0.8125rem;
  color: #6c757d;
  margin-top: 0.25rem;
  font-weight: 400;
}

.total-value {
  font-size: 2rem;
  font-weight: 700;
  color: #28a745;
  white-space: nowrap;
}

/* Observações */
.obs-item {
  margin-bottom: 1.5rem;
}

.obs-item label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.obs-content {
  padding: 1rem;
  background: #f8f9fa;
  border-left: 3px solid #6c757d;
  border-radius: 0.25rem;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: #495057;
  white-space: pre-wrap;
}

/* Code elements */
code {
  background: #f8f9fa;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  color: #495057;
  font-weight: 500;
}

/* Utility */
.mr-1 {
  margin-right: 0.25rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.ml-1 {
  margin-left: 0.25rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.text-danger {
  color: #dc3545 !important;
  font-weight: 600;
}

.text-warning {
  color: #ffc107 !important;
  font-weight: 600;
}

.text-success {
  color: #28a745 !important;
  font-weight: 600;
}

.text-muted {
  color: #6c757d;
}
</style>
